import { CheckCircle2, Download, ExternalLink, Plug, RefreshCw, Unplug, Upload } from 'lucide-react'
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { apiErrorMessage } from '../../api/client'
import { aggregatorDpgApi, type DpgBulkUpload, type DpgConnection } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'

/**
 * The partner portal's aggregator-dpg (Blue Dots) section: connect the coordinator's account there
 * once, then use its features - profile, onboarding links, bulk uploads, onboarding stats, the
 * dashboard, campaigns and support - all through aggregator-dpg's own API (AggregatorDpgService).
 */
type Section = 'profile' | 'links' | 'uploads' | 'stats' | 'dashboard' | 'campaigns' | 'support'

const SECTIONS: { id: Section; label: string; campaign?: boolean }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'links', label: 'Onboarding links' },
  { id: 'uploads', label: 'Bulk upload' },
  { id: 'stats', label: 'Onboarding stats' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'campaigns', label: 'Campaigns', campaign: true },
  { id: 'support', label: 'Support' },
]

const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-'

/** Saves a blob the browser already holds, under the given name. */
function save(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/** A value from aggregator-dpg's answer, shown plainly whatever its type. */
function show(v: unknown): string {
  if (v === null || v === undefined || v === '') return '-'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

export function DpgTab() {
  const [connections, setConnections] = useState<DpgConnection[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [section, setSection] = useState<Section>('profile')

  const load = useCallback(async () => {
    try {
      setConnections(await aggregatorDpgApi.status())
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load your aggregator-dpg connection'))
    }
  }, [])

  useEffect(() => {
    void load()
    // Back from Keycloak: ?dpg=connected|denied|error on the portal's address.
    const result = new URLSearchParams(window.location.search).get('dpg')
    if (result === 'connected') setNotice('Your aggregator-dpg account is connected.')
    else if (result === 'denied') setError('The sign-in on aggregator-dpg was cancelled.')
    else if (result === 'error') setError('Connecting to aggregator-dpg did not complete. Please try again.')
  }, [load])

  const portal = connections?.find((c) => c.purpose === 'PORTAL')
  const campaign = connections?.find((c) => c.purpose === 'CAMPAIGN')

  async function connect(purpose: DpgConnection['purpose']) {
    setError(null)
    try {
      const { url } = await aggregatorDpgApi.connect(purpose)
      window.location.href = url
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not start the aggregator-dpg sign-in'))
    }
  }

  async function disconnect(purpose: DpgConnection['purpose']) {
    setError(null)
    try {
      await aggregatorDpgApi.disconnect(purpose)
      await load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not disconnect'))
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-ink-900">Aggregator DPG</h2>
        <p className="mt-1 text-sm text-ink-500">
          Use your Blue Dots aggregator-dpg account from here. Connect it once - you sign in on aggregator-dpg with your
          own one-time code - and the portal works with it on your behalf.
        </p>
      </div>

      <ErrorMessage message={error} />
      {notice && <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}

      <div className="grid gap-3 sm:grid-cols-2">
        <ConnectionCard
          title="aggregator-dpg account"
          detail="Profile, onboarding links, bulk uploads, stats, dashboard and support."
          connection={portal}
          onConnect={() => void connect('PORTAL')}
          onDisconnect={() => void disconnect('PORTAL')}
        />
        <ConnectionCard
          title="Campaigns"
          detail="Email, voice calls and contact exports to the people you onboarded."
          connection={campaign}
          onConnect={() => void connect('CAMPAIGN')}
          onDisconnect={() => void disconnect('CAMPAIGN')}
        />
      </div>

      {portal?.connected && (
        <>
          <nav className="flex flex-wrap gap-1 border-b border-ink-200 pb-2">
            {SECTIONS.filter((s) => !s.campaign || campaign?.connected).map((s) => (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                aria-current={section === s.id ? 'page' : undefined}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                  section === s.id ? 'bg-ink-900 text-ink-50' : 'text-ink-600 hover:bg-ink-100'
                }`}
              >
                {s.label}
              </button>
            ))}
          </nav>
          {section === 'profile' && <ProfileSection />}
          {section === 'links' && <LinksSection />}
          {section === 'uploads' && <UploadsSection />}
          {section === 'stats' && <StatsSection />}
          {section === 'dashboard' && <DashboardSection />}
          {section === 'campaigns' && campaign?.connected && <CampaignsSection />}
          {section === 'support' && <SupportSection />}
        </>
      )}
    </div>
  )
}

function ConnectionCard({
  title,
  detail,
  connection,
  onConnect,
  onDisconnect,
}: {
  title: string
  detail: string
  connection?: DpgConnection
  onConnect: () => void
  onDisconnect: () => void
}) {
  const approved = connection?.decision === 'approved'
  return (
    <div className="card-pad">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold text-ink-900">{title}</div>
        {connection?.connected && (
          <span className="flex items-center gap-1 text-xs font-semibold text-green-700">
            <CheckCircle2 size={14} /> Connected
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-ink-500">{detail}</p>
      {!connection ? (
        <p className="mt-3 text-sm text-ink-500">Loading...</p>
      ) : !connection.configured ? (
        <p className="mt-3 rounded-lg bg-ink-100 px-3 py-2 text-sm text-ink-600">
          Not set up on this server yet - waiting for the aggregator-dpg sign-in details.
        </p>
      ) : connection.connected ? (
        <div className="mt-3 space-y-2 text-sm">
          <div className="text-ink-600">
            Since {fmt(connection.connectedAt)}
            {connection.dpgAggregatorId && <> &middot; id {connection.dpgAggregatorId}</>}
          </div>
          {!approved && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">
              aggregator-dpg has not approved this account yet, so most features will refuse until it does.
            </p>
          )}
          <button onClick={onDisconnect} className="btn-ghost gap-1.5">
            <Unplug size={14} /> Disconnect
          </button>
        </div>
      ) : (
        <button onClick={onConnect} className="btn-sun mt-3 gap-1.5">
          <Plug size={15} /> Connect
        </button>
      )}
    </div>
  )
}

/** Loads one aggregator-dpg answer and shows it, with an error line and a reload. */
function useDpg<T>(path: string | null) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const reload = useCallback(async () => {
    if (!path) return
    setLoading(true)
    setError(null)
    try {
      setData(await aggregatorDpgApi.get<T>(path))
    } catch (err) {
      setError(apiErrorMessage(err, 'aggregator-dpg did not answer'))
    } finally {
      setLoading(false)
    }
  }, [path])
  useEffect(() => {
    void reload()
  }, [reload])
  return { data, error, loading, reload, setError }
}

function Panel({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="card-pad space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold text-ink-900">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  )
}

function Rows({ rows }: { rows: [string, unknown][] }) {
  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt className="text-xs font-semibold uppercase tracking-wide text-ink-400">{k}</dt>
          <dd className="break-words text-ink-900">{show(v)}</dd>
        </div>
      ))}
    </dl>
  )
}

// ---- Profile --------------------------------------------------------------------------------

type DpgProfile = Record<string, unknown> & {
  org_name?: string
  status?: string
  type?: string
  url?: string
  contact?: Record<string, unknown>
  identity?: Record<string, unknown>
}

function ProfileSection() {
  const { data, error, loading } = useDpg<DpgProfile>('/v1/aggregators/profile/me')
  return (
    <Panel title="Your aggregator-dpg profile">
      <ErrorMessage message={error} />
      {loading && <p className="text-sm text-ink-500">Loading...</p>}
      {data && (
        <Rows
          rows={[
            ['Organisation', data.org_name],
            ['Status', data.status],
            ['Type', data.type],
            ['Website', data.url],
            ['Contact name', data.contact?.name],
            ['Contact email', data.contact?.email],
            ['Contact phone', data.contact?.phone],
            ['Signed in as', [data.identity?.first_name, data.identity?.last_name].filter(Boolean).join(' ')],
          ]}
        />
      )}
    </Panel>
  )
}

// ---- Onboarding links -----------------------------------------------------------------------

interface DpgLink {
  link_id: string
  slug: string
  domain: string
  status: 'draft' | 'live' | 'retired'
  public_url?: string | null
  qr_url?: string | null
  expires_at?: string | null
  metrics?: { total?: number; passed?: number; failed?: number; skipped?: number }
}

function LinksSection() {
  const { data, error, loading, reload, setError } = useDpg<{ items: DpgLink[]; total: number }>('/v1/links?limit=100')
  const [domain, setDomain] = useState('')
  const [slugText, setSlugText] = useState('')
  const [busy, setBusy] = useState(false)

  async function create(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await aggregatorDpgApi.post('/v1/links/create', {
        domain: domain.trim(),
        ...(slugText.trim() ? { slug: slugText.trim() } : {}),
        status: 'live',
      })
      setDomain('')
      setSlugText('')
      await reload()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create the link'))
    } finally {
      setBusy(false)
    }
  }

  async function toggle(link: DpgLink) {
    setError(null)
    try {
      await aggregatorDpgApi.post(`/v1/links/${link.link_id}/${link.status === 'live' ? 'deactivate' : 'activate'}`)
      await reload()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not change the link'))
    }
  }

  return (
    <Panel
      title="Onboarding links"
      action={
        <button onClick={() => void reload()} className="btn-ghost gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      <form onSubmit={create} className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="label label-required">Domain</span>
          <input value={domain} onChange={(e) => setDomain(e.target.value)} required className="input" placeholder="e.g. student" />
        </label>
        <label className="block">
          <span className="label">Link name (optional)</span>
          <input value={slugText} onChange={(e) => setSlugText(e.target.value)} className="input" placeholder="e.g. delhi-camp" />
        </label>
        <button type="submit" disabled={busy} className="btn-sun">
          {busy ? 'Creating...' : 'Create link'}
        </button>
      </form>
      <ErrorMessage message={error} />
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <th className="py-2 pr-3">Link</th>
              <th className="py-2 pr-3">Domain</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Sign-ups</th>
              <th className="py-2 pr-3">Expires</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(data?.items ?? []).map((l) => (
              <tr key={l.link_id}>
                <td className="py-2 pr-3">
                  <div className="font-medium text-ink-900">{l.slug}</div>
                  {l.public_url && (
                    <a href={l.public_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-ink-600 hover:underline">
                      Open <ExternalLink size={11} />
                    </a>
                  )}
                </td>
                <td className="py-2 pr-3 text-ink-700">{l.domain}</td>
                <td className="py-2 pr-3 text-ink-700">{l.status}</td>
                <td className="py-2 pr-3 text-ink-700">
                  {l.metrics?.passed ?? 0} of {l.metrics?.total ?? 0}
                </td>
                <td className="py-2 pr-3 text-ink-600">{fmt(l.expires_at)}</td>
                <td className="py-2 text-right">
                  {l.status !== 'retired' && (
                    <button onClick={() => void toggle(l)} className="text-sm font-semibold text-ink-900 hover:underline">
                      {l.status === 'live' ? 'Retire' : 'Make live'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && (data?.items ?? []).length === 0 && (
              <tr>
                <td colSpan={6} className="py-4 text-center text-ink-500">
                  No links yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

// ---- Bulk upload ----------------------------------------------------------------------------

function UploadsSection() {
  const { data, error, reload, setError } = useDpg<{ items?: DpgBulkUpload[] } | DpgBulkUpload[]>('/v1/bulk-uploads?limit=50')
  const [type, setType] = useState<'seeker' | 'provider'>('seeker')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const uploads = Array.isArray(data) ? data : (data?.items ?? [])

  async function template() {
    setError(null)
    try {
      save(await aggregatorDpgApi.download(`/v1/bulk-uploads/template?participant_type=${type}&format=csv`), `${type}-template.csv`)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not download the template'))
    }
  }

  async function upload(e: FormEvent) {
    e.preventDefault()
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      await aggregatorDpgApi.bulkUpload(type, file)
      setFile(null)
      await reload()
    } catch (err) {
      setError(apiErrorMessage(err, 'The upload did not go through'))
    } finally {
      setBusy(false)
    }
  }

  async function errors(id: string) {
    setError(null)
    try {
      const { url } = await aggregatorDpgApi.get<{ url: string }>(`/v1/bulk-uploads/${id}/errors.csv`)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      setError(apiErrorMessage(err, 'The error file is not ready'))
    }
  }

  return (
    <Panel
      title="Bulk upload to aggregator-dpg"
      action={
        <button onClick={() => void reload()} className="btn-ghost gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      <p className="text-sm text-ink-500">
        CSV only. Download the template for the columns aggregator-dpg expects; it checks every row and keeps a file of
        the rows it could not take.
      </p>
      <form onSubmit={upload} className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="label">Who is in the file</span>
          <select value={type} onChange={(e) => setType(e.target.value as 'seeker' | 'provider')} className="input">
            <option value="seeker">Learners (seekers)</option>
            <option value="provider">Tutors (providers)</option>
          </select>
        </label>
        <button type="button" onClick={() => void template()} className="btn-ghost gap-1.5">
          <Download size={14} /> Template
        </button>
        <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
        <button type="submit" disabled={!file || busy} className="btn-sun gap-1.5 disabled:opacity-40">
          <Upload size={15} /> {busy ? 'Uploading...' : 'Upload'}
        </button>
      </form>
      <ErrorMessage message={error} />
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <th className="py-2 pr-3">Uploaded</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Rows</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {uploads.map((u) => (
              <tr key={u.upload_id}>
                <td className="py-2 pr-3 text-ink-700">{fmt(u.created_at)}</td>
                <td className="py-2 pr-3 text-ink-700">{u.participant_type}</td>
                <td className="py-2 pr-3 text-ink-700">
                  {u.status}
                  {u.status_reason && <div className="text-xs text-ink-500">{u.status_reason}</div>}
                </td>
                <td className="py-2 pr-3 text-ink-700">
                  {u.total_rows ?? '-'} total &middot; {u.passed ?? 0} in &middot; {u.failed ?? 0} failed &middot; {u.skipped ?? 0} skipped
                </td>
                <td className="py-2 text-right">
                  {(u.failed ?? 0) > 0 && u.status === 'completed' && (
                    <button onClick={() => void errors(u.upload_id)} className="text-sm font-semibold text-ink-900 hover:underline">
                      Failed rows
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {uploads.length === 0 && (
              <tr>
                <td colSpan={5} className="py-4 text-center text-ink-500">
                  No uploads yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

// ---- Onboarding stats -----------------------------------------------------------------------

interface DpgSummary {
  total: number
  passed: number
  failed: number
  skipped: number
}

function StatsSection() {
  const summary = useDpg<DpgSummary>('/v1/onboarding/summary')
  const bySource = useDpg<{ by_source: (DpgSummary & { source: string })[] }>('/v1/onboarding/by-source')
  return (
    <Panel title="Onboarding stats">
      <ErrorMessage message={summary.error ?? bySource.error} />
      {summary.data && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(['total', 'passed', 'failed', 'skipped'] as const).map((k) => (
            <div key={k} className="rounded-lg bg-ink-50 px-3 py-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-500">{k}</div>
              <div className="text-xl font-bold text-ink-900">{summary.data?.[k] ?? 0}</div>
            </div>
          ))}
        </div>
      )}
      {bySource.data && bySource.data.by_source.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <th className="py-2 pr-3">Source</th>
              <th className="py-2 pr-3">Total</th>
              <th className="py-2 pr-3">In</th>
              <th className="py-2 pr-3">Failed</th>
              <th className="py-2">Skipped</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {bySource.data.by_source.map((s) => (
              <tr key={s.source}>
                <td className="py-2 pr-3 text-ink-900">{s.source}</td>
                <td className="py-2 pr-3">{s.total}</td>
                <td className="py-2 pr-3">{s.passed}</td>
                <td className="py-2 pr-3">{s.failed}</td>
                <td className="py-2">{s.skipped}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}

// ---- Dashboard ------------------------------------------------------------------------------

function DashboardSection() {
  const [page, setPage] = useState(1)
  const { data, error, loading, reload, setError } = useDpg<Record<string, unknown>>(`/v1/dashboard?page=${page}&limit=50`)
  // aggregator-dpg passes the network's payload through without a fixed shape: the people are its
  // items, everything else is a summary.
  const items = (Array.isArray(data?.items) ? data?.items : []) as Record<string, unknown>[]
  const summary = Object.entries(data ?? {}).filter(([k, v]) => k !== 'items' && (typeof v !== 'object' || v === null))
  const columns = items.length > 0 ? Object.keys(items[0]).filter((k) => typeof items[0][k] !== 'object').slice(0, 8) : []

  async function exportCsv() {
    setError(null)
    try {
      save(await aggregatorDpgApi.download('/v1/dashboard/export'), 'aggregator-dpg-dashboard.csv')
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not export'))
    }
  }

  return (
    <Panel
      title="Dashboard"
      action={
        <div className="flex gap-2">
          <button onClick={() => void reload()} className="btn-ghost gap-1.5">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => void exportCsv()} className="btn-ghost gap-1.5">
            <Download size={14} /> Export CSV
          </button>
        </div>
      }
    >
      <ErrorMessage message={error} />
      {loading && <p className="text-sm text-ink-500">Loading...</p>}
      {summary.length > 0 && <Rows rows={summary} />}
      {columns.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-ink-500">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="py-2 pr-3">
                    {c.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {items.map((it, i) => (
                <tr key={show(it.item_id ?? it.id ?? i)}>
                  {columns.map((c) => (
                    <td key={c} className="py-2 pr-3 text-ink-700">
                      {show(it[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end gap-2 text-sm">
        <button className="btn-ghost" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>
        <button className="btn-ghost" disabled={items.length < 50} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </div>
    </Panel>
  )
}

// ---- Campaigns ------------------------------------------------------------------------------

type Channel = 'email' | 'voice' | 'export'

interface DpgJob {
  job_id: string
  status: string
  counts?: Record<string, number>
  created_at: string
}

function CampaignsSection() {
  const [channel, setChannel] = useState<Channel>('email')
  const jobs = useDpg<{ jobs: DpgJob[] }>(`/v1/campaign/${channel}?limit=20`)
  const [ids, setIds] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [agentId, setAgentId] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  async function send(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setNotice(null)
    jobs.setError(null)
    const itemIds = ids.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
    const content =
      channel === 'email' ? { subject, body_markdown: body } : channel === 'voice' ? { agent_id: agentId } : undefined
    try {
      await aggregatorDpgApi.post(`/v1/campaign/${channel}`, { item_ids: itemIds, ...(content ? { content } : {}) })
      setNotice(`Queued for ${itemIds.length} ${itemIds.length === 1 ? 'person' : 'people'}.`)
      await jobs.reload()
    } catch (err) {
      jobs.setError(apiErrorMessage(err, 'Could not start the campaign'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel title="Campaigns">
      <div className="flex gap-1">
        {(['email', 'voice', 'export'] as const).map((c) => (
          <button
            key={c}
            onClick={() => setChannel(c)}
            className={`rounded-full px-3 py-1 text-sm font-semibold ${channel === c ? 'bg-ink-900 text-ink-50' : 'bg-ink-100 text-ink-600'}`}
          >
            {c === 'email' ? 'Email' : c === 'voice' ? 'Voice call' : 'Contact export'}
          </button>
        ))}
      </div>
      <form onSubmit={send} className="space-y-3">
        <label className="block">
          <span className="label label-required">People (item ids from the dashboard)</span>
          <textarea value={ids} onChange={(e) => setIds(e.target.value)} required rows={3} className="input" placeholder="One per line, or comma separated" />
        </label>
        {channel === 'email' && (
          <>
            <label className="block">
              <span className="label label-required">Subject</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} required maxLength={200} className="input" />
            </label>
            <label className="block">
              <span className="label label-required">Message (Markdown)</span>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={6} className="input" />
              <span className="mt-1 block text-xs text-ink-500">
                {'You can use {{name}}, {{first_name}}, {{last_name}}, {{email}} and {{phone}}.'}
              </span>
            </label>
          </>
        )}
        {channel === 'voice' && (
          <label className="block">
            <span className="label label-required">Voice agent id</span>
            <input value={agentId} onChange={(e) => setAgentId(e.target.value)} required className="input" />
          </label>
        )}
        {channel === 'export' && (
          <p className="text-sm text-ink-500">A file of their names, emails and phones is emailed to you.</p>
        )}
        <button type="submit" disabled={busy} className="btn-sun">
          {busy ? 'Sending...' : 'Start campaign'}
        </button>
      </form>
      <ErrorMessage message={jobs.error} />
      {notice && <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-ink-500">
          <tr>
            <th className="py-2 pr-3">Started</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2">Counts</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {(jobs.data?.jobs ?? []).map((j) => (
            <tr key={j.job_id}>
              <td className="py-2 pr-3 text-ink-700">{fmt(j.created_at)}</td>
              <td className="py-2 pr-3 text-ink-700">{j.status}</td>
              <td className="py-2 text-xs text-ink-600">
                {Object.entries(j.counts ?? {})
                  .filter(([, n]) => n > 0)
                  .map(([k, n]) => `${k.replace(/_/g, ' ')} ${n}`)
                  .join(' · ') || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  )
}

// ---- Support --------------------------------------------------------------------------------

interface SupportConfig {
  enabled: boolean
  maxTotalBytes: number
  maxFiles: number
  allowedExtensions: string[]
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

function SupportSection() {
  const config = useDpg<SupportConfig>('/v1/support/config')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [type, setType] = useState<'support_request' | 'complaint'>('support_request')
  const [details, setDetails] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [reference, setReference] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    config.setError(null)
    try {
      const attachments = await Promise.all(
        files.map(async (f) => ({ filename: f.name, contentType: f.type || 'application/octet-stream', data: await toBase64(f) })),
      )
      const res = await aggregatorDpgApi.post<{ reference: string }>('/v1/support', {
        name,
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        type,
        details,
        consent: true,
        ...(attachments.length ? { attachments } : {}),
      })
      setReference(res.reference)
      setDetails('')
      setFiles([])
    } catch (err) {
      config.setError(apiErrorMessage(err, 'Could not send your request'))
    } finally {
      setBusy(false)
    }
  }

  if (config.data && !config.data.enabled) {
    return (
      <Panel title="Support">
        <p className="text-sm text-ink-500">Support requests are switched off on aggregator-dpg.</p>
      </Panel>
    )
  }

  return (
    <Panel title="Support">
      {reference && (
        <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">
          Sent. Your reference is <strong>{reference}</strong>.
        </p>
      )}
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="label label-required">Your name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required className="input" />
        </label>
        <label className="block">
          <span className="label">Type</span>
          <select value={type} onChange={(e) => setType(e.target.value as 'support_request' | 'complaint')} className="input">
            <option value="support_request">Support request</option>
            <option value="complaint">Complaint</option>
          </select>
        </label>
        <label className="block">
          <span className="label">Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" />
        </label>
        <label className="block">
          <span className="label">Phone</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className="input" />
        </label>
        <label className="block sm:col-span-2">
          <span className="label label-required">What do you need?</span>
          <textarea value={details} onChange={(e) => setDetails(e.target.value)} required maxLength={5000} rows={5} className="input" />
        </label>
        <label className="block sm:col-span-2">
          <span className="label">Attachments</span>
          <input
            type="file"
            multiple
            accept={config.data?.allowedExtensions?.join(',')}
            onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, config.data?.maxFiles ?? 5))}
            className="text-sm"
          />
        </label>
        <div className="sm:col-span-2">
          <ErrorMessage message={config.error} />
          <button type="submit" disabled={busy} className="btn-sun mt-2">
            {busy ? 'Sending...' : 'Send to support'}
          </button>
        </div>
      </form>
    </Panel>
  )
}
